import { useCallback, useEffect, useRef } from 'react';
import {
    Avatar,
    Flex,
    Grid,
    Header,
    pxArrayToRem,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './Heading.style';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { Link } from '@remix-run/react';

const MIN_SCALE = 0.88;
const MAX_SCALE = 1;

interface HeadingProps {
    data: ProductPageLoaderData['product']['Professional'];
}

/**
 * Continuously scales an element based on its scroll position in the viewport.
 * The element starts at {@link MIN_SCALE} when entering from the bottom and
 * reaches {@link MAX_SCALE} as it approaches the vertical centre. Scrolling
 * back reverses the effect. Updates are batched via requestAnimationFrame to
 * keep the animation smooth without triggering React re-renders.
 */
const useScrollZoom = () => {
    const ref = useRef<HTMLDivElement>(null);
    const ticking = useRef(false);

    const updateScale = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;

        // progress: 0 when element top is at the viewport bottom,
        //           1 when element top reaches ~30 % from the top.
        const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh * 0.7)));

        const scale = MIN_SCALE + (MAX_SCALE - MIN_SCALE) * progress;
        el.style.transform = `scale(${scale})`;
        ticking.current = false;
    }, []);

    const handleScroll = useCallback(() => {
        if (!ticking.current) {
            ticking.current = true;
            requestAnimationFrame(updateScale);
        }
    }, [updateScale]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        updateScale();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll, updateScale]);

    return { ref };
};

const Heading = ({ data }: HeadingProps) => {
    const logo = data?.CompanyLogo;
    const logoBackgroundColor = data?.LogoBackgroundColor || 'white';
    const { css } = useStyles();
    const { ref: logoRef } = useScrollZoom();

    return (
        <Flex column={false} vAlign="center" className={css(S.HeaderContainer)}>
            <Flex
                column={false}
                vAlign="center"
                className={css(S.HeaderContainerInner)}
            >
                <Grid
                    columns={pxArrayToRem([880, 896])}
                    className={css({
                        columnGap: pxToRem(44),
                    })}
                >
                    <Header as="h3" variant="03" className={css(S.Header)}>
                        About the
                        <br />
                        Seller
                    </Header>

                    {logo && (
                        <div className={css(S.CompanyLogoWrapper)}>
                            <div className={css(S.CompanyLogoContainer)}>
                                <div
                                    ref={logoRef}
                                    className="inline-block origin-center will-change-transform"
                                >
                                    <Avatar
                                        as={Link}
                                        to={data.Link}
                                        image={{
                                            src: logo ?? '',
                                            alt: `${data.Title} company logo`,
                                            loading: 'lazy',
                                        }}
                                        initials={'Logo'}
                                        size={230}
                                        imageFit={'contain'}
                                        styles={{
                                            borderWidth: 0,
                                        }}
                                        shape={'square'}
                                        variables={{
                                            avatarImageBackgroundColor:
                                                logoBackgroundColor,
                                            borderRadius: '0',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Grid>
            </Flex>
        </Flex>
    );
};

export default Heading;
